/**
 * Canvas 2D field renderer (HANDOFF §4.1 / §6 Phase 2 Agent D).
 *
 * Reads sim state and DRAWS it. It must NEVER mutate sim state, never emit input,
 * never import input/ui/app/sim-reducer code (§4.1). Rendering is a pure read off
 * the fixed-step state, interpolated for smoothness (alpha ∈ [0,1), §4.2).
 *
 * The sim lays everything out in a VIRTUAL FIELD SPACE of 440×900 (FIELD in
 * constants.ts): greens, tees, pins, hazards and balls all carry field-space
 * coordinates. This renderer maps field-space → screen with a single uniform
 * scale + offset (LETTERBOX to the 440:900 aspect, centred, safe-area aware) and
 * draws on a HiDPI backing store. Because every coordinate already lives in field
 * space, the renderer never re-derives geometry — it just transforms and paints.
 *
 * Visual reference: fairway-clash-prototype.html (mined for look, NOT architecture).
 * Palette: HANDOFF §8 / src/styles/tokens.css — hex values are duplicated here with
 * a `// §8` citation because Canvas 2D cannot read CSS custom properties cheaply
 * per-frame; tokens.css remains the source of truth.
 *
 * Performance (§7): no per-frame allocations in the hot path. Scratch objects,
 * the field-space dimensions and re-used arrays are hoisted; gradients that depend
 * only on layout are cached and only rebuilt on resize / when their inputs change.
 */
import type {
  Ball,
  Club,
  Fairway,
  GameState,
  Green,
  Hazard,
  ScoreResult,
  SwingState,
  TeeBox,
  Vec2,
} from '../sim/types.ts';
import { BALL_REST_SECONDS, BALL_SINK_SECONDS, CARD_MAP, FIELD, FLIGHT, GOPHER, ROLL, SCORING, SHAPE, SHOT_RANGE, SWING, TEE_KEYS, TREE, boxForPower, powerBandHalf } from '../sim/constants.ts';
import { sampleGreenOutline } from '../sim/greens.ts';
import type { SpriteSet } from './sprites.ts';

// ---------------------------------------------------------------------------
// Public seam (overseer wires these into app/main.ts)
// ---------------------------------------------------------------------------

export interface Viewport {
  readonly width: number;
  readonly height: number;
  readonly dpr: number;
}

export interface Renderer {
  /** Draw one frame from a read-only view of sim state. `alpha` = fixed-step interp [0,1). */
  render(state: GameState, alpha: number): void;
  /** Re-read canvas CSS size + device pixel ratio and rebuild the backing store. */
  resize(): void;
  /** Map a client (CSS-pixel) point to field space — for input (Agent E) via main. */
  screenToField(clientX: number, clientY: number): Vec2;
  /** Map a field-space point to client (CSS-pixel) screen coordinates. */
  fieldToScreen(p: Vec2): { x: number; y: number };
  /** Supply the loaded pixel-art sprite set (or null → procedural drawing). */
  setSprites(sprites: SpriteSet | null): void;
  /** Left-handed player → flip YOUR golfer so the right shoulder leads to the hole. */
  setLeftHanded(left: boolean): void;
}

// ---------------------------------------------------------------------------
// §8 design tokens (mirrored from tokens.css — that file is the source of truth)
// ---------------------------------------------------------------------------

const TOKENS = {
  turfDeep: '#0e3b2e', // §8 --turf-deep
  turf: '#1c6e4a', // §8 --turf
  turfLit: '#34a96b', // §8 --turf-lit
  fairway: '#7cc47f', // §8 --fairway
  sand: '#e8d6a8', // §8 --sand
  sandDark: '#c9b27c', // §8 --sand-dark
  water: '#3d8bd4', // §8 --water
  waterDark: '#1f5d96', // §8 --water-dark
  flag: '#e0463a', // §8 --flag
  flagDark: '#b22f26', // §8 --flag-dark
  dusk: '#10202e', // §8 --dusk
  dusk2: '#1a3145', // §8 --dusk-2
  cream: '#f4efe2', // §8 --cream
  ink: '#0c1410', // §8 --ink
  gold: '#f2c14e', // §8 --gold
} as const;

/**
 * Per-side play-area THEME. The field splits at the midline: the OPPONENT's half
 * (top) is two LIGHTER green shades with light "alt green" greens/fairway/tees; YOUR
 * half (bottom) is the same scheme two shades DARKER. Purely cosmetic — geometry,
 * scoring and §5b are untouched.
 */
const THEME = {
  rival: {
    // opponent half — brighter arcade greens (top). turfA = rough base, turfB = darker tuft fleck.
    turfA: '#57b15f',
    turfB: '#3f9850',
    // surface green for greens / fairway / tees (pops against the rough)
    feature: '#83d873',
    collar: '#57a851',
    teeTop: '#79cc6b',
    teeBot: '#4f9e4c',
  },
  you: {
    // my half — the same arcade palette, a touch deeper (bottom)
    turfA: '#41a050',
    turfB: '#2f8540',
    feature: '#6cc861',
    collar: '#458f43',
    teeTop: '#60b455',
    teeBot: '#3c853c',
  },
} as const;

/** Linear blend between two #rrggbb hex colors → an `rgb(r,g,b)` string. */
function mixHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const r = Math.round(((pa >> 16) & 255) + (((pb >> 16) & 255) - ((pa >> 16) & 255)) * t);
  const g = Math.round(((pa >> 8) & 255) + (((pb >> 8) & 255) - ((pa >> 8) & 255)) * t);
  const bl = Math.round((pa & 255) + ((pb & 255) - (pa & 255)) * t);
  return `rgb(${r},${g},${bl})`;
}

/** Smooth 0→1 Hermite ramp, clamped (eases the play-area cross-fade). */
function smoothstep(x: number): number {
  const v = x < 0 ? 0 : x > 1 ? 1 : x;
  return v * v * (3 - 2 * v);
}

// --- Pixel rough-grass texture (painted ONCE per resize into a BUFFER-resolution canvas) -------
/**
 * Paint flat, tufted PIXEL grass into an offscreen context at the pixel-BUFFER resolution (so each
 * speck is a crisp 1px tuft, never a downscaled blur). Two flat tone zones — the rival half (top)
 * over your half (bottom) — meet at `splitY` with a 2-row dithered seam, then a deterministic
 * scatter of 1px tufts (a darker fleck or a lighter highlight) gives the 8-bit grass texture.
 */
function paintGrass(g: CanvasRenderingContext2D, tw: number, th: number, splitY: number): void {
  const top = THEME.rival.turfA;
  const bot = THEME.you.turfA;
  const topTuftLite = mixHex(top, '#eaffd6', 0.3);
  const topTuftDark = THEME.rival.turfB;
  const botTuftLite = mixHex(bot, '#d8ffc4', 0.26);
  const botTuftDark = THEME.you.turfB;

  // flat two-tone halves
  g.fillStyle = top;
  g.fillRect(0, 0, tw, splitY);
  g.fillStyle = bot;
  g.fillRect(0, splitY, tw, Math.max(0, th - splitY));

  // a 2-row dithered seam at the midline (checkerboard hand-off, no smooth gradient)
  for (let y = splitY - 1; y <= splitY; y++) {
    if (y < 0 || y >= th) continue;
    g.fillStyle = y < splitY ? bot : top;
    for (let x = y & 1; x < tw; x += 2) g.fillRect(x, y, 1, 1);
  }

  // deterministic 1px grass tufts — a darker fleck or a lighter highlight, per half
  const hash = (n: number): number => {
    const s = Math.sin(n * 12.9898 + 4.1414) * 43758.5453;
    return s - Math.floor(s);
  };
  const count = Math.round(tw * th * 0.085); // ~8.5% speckle
  let i = 1;
  for (let k = 0; k < count; k++) {
    const x = Math.floor(hash(i * 1.73) * tw);
    const y = Math.floor(hash(i * 2.31) * th);
    const onTop = y < splitY;
    g.fillStyle = hash(i * 3.11) < 0.5 ? (onTop ? topTuftDark : botTuftDark) : onTop ? topTuftLite : botTuftLite;
    g.fillRect(x, y, 1, 1);
    i += 1;
  }
}

// ---------------------------------------------------------------------------
// Hoisted scratch (no per-frame allocation in the hot path — §7)
// ---------------------------------------------------------------------------

/**
 * Pooled mutable points returned by the public transforms (one live value each).
 * Hoisted to module scope so screenToField / fieldToScreen allocate nothing in
 * the input-handling hot path (§7). Callers must read the result immediately.
 */
const scratchField = { x: 0, y: 0 };
const screenOut = { x: 0, y: 0 };

const TWO_PI = Math.PI * 2;

/**
 * Reusable field-space outline buffers for the green SHAPE (collar + surface), so
 * `drawGreen` allocates nothing in the hot path (§7): each is re-filled per green
 * per frame by {@link sampleGreenOutline}. Two buffers because the collar is the
 * surface outline expanded — both are sampled each green draw.
 */
const surfaceOutline: { x: number; y: number }[] = [];
const collarOutline: { x: number; y: number }[] = [];
for (let i = 0; i < SHAPE.outlineSegments; i++) {
  surfaceOutline.push({ x: 0, y: 0 });
  collarOutline.push({ x: 0, y: 0 });
}

/**
 * Hoisted scratch {@link ShapeRegion} view of a hazard (its `pos` becomes `center`)
 * so `drawHazard` can trace the hazard's procedural outline with the SHARED sampler
 * without allocating per frame (§7). Re-filled per hazard.
 */
const hazardShape: {
  center: { x: number; y: number };
  radius: number;
  rx: number;
  ry: number;
  rot: number;
  lobe: number;
  lobePhase: number;
} = { center: { x: 0, y: 0 }, radius: 0, rx: 0, ry: 0, rot: 0, lobe: 0, lobePhase: 0 };

/** Collar expansion past the green surface, in field-space px (≈11px in the brief). */
const COLLAR_PX = 11;

/**
 * 8-BIT PIXEL SIZE — the single knob for the retro look. The whole scene is drawn into an
 * offscreen buffer at 1/PIXEL the canvas's linear resolution, then NEAREST-NEIGHBOR upscaled onto
 * the canvas, so each rendered "pixel" ends up ≈ PIXEL CSS-px square. Bigger = chunkier / more
 * retro; smaller = finer. Browser-only — the headless render harness draws straight to the canvas.
 */
const PIXEL = 3;

/**
 * With REAL pixel-art sprites we don't want the heavy low-res downscale buffer (it would crunch the
 * art); instead we render at full resolution and rely on the ART being pixel art + crisp
 * (nearest-neighbor) image scaling. Flip to `true` to bring back the procedural-pixel chunk buffer.
 */
const USE_PIXEL_BUFFER = false;

/** Sprite sizing (field-space units / tile sizes), tunable from on-device feedback. */
const SPRITE = {
  golferFieldH: 40, // golfer sprite height in field units
  treeScale: 2.2, // tree sprite drawn at (tree radius × this)
  tileRough: 88, // field-units per rough-grass tile
  tileFairway: 78, // per fairway tile
  tileGreen: 70, // per putting-green tile
  tileHazard: 60, // per water/sand hazard tile
  flagH: 46, // flag/pin height in field units (pole base anchored at the cup)
  flagPoleFracX: 0.18, // where the pole sits across the flag sprite (0=left)
  flagPoleFracY: 0.98, // pole base near the sprite bottom
  ballR: 7, // ball sprite drawn at this radius (field units)
  crowdH: 16, // spectator sprite height (field units)
  gopherH: 30, // gopher sprite height (field units)
  teePadW: 46, // tee-pad sprite width (field units)
} as const;


/**
 * Swing-meter sweet-spot colours. Each band HUE-SHIFTS and brightens toward its
 * PERFECT centre (edge → core → edge gradient). POWER is BLUE so it never blends
 * into the green turf; IMPACT is gold. The marker/lock ticks match the active core.
 */
const METER = {
  powerEdge: 'rgba(37,99,235,0.40)', // deep blue at the band edges
  powerCore: 'rgba(96,205,255,0.96)', // bright cyan-blue at the perfect centre
  powerMark: '#5fcdff',
  impactEdge: 'rgba(234,140,12,0.55)', // amber at the edges
  impactCore: 'rgba(255,233,99,0.98)', // bright yellow at the perfect centre
  impactMark: '#ffe963',
} as const;

/** Floating score-popup copy + style per result (§3.5): celebratory for good scores,
 *  muted for misses. `size` is the field-px font size (ace is biggest, most emphatic). */
const SCORE_POPUP: Record<ScoreResult, { text: string; color: string; size: number }> = {
  ace: { text: 'HOLE IN ONE!!!', color: '#ffe14d', size: 19 },
  eagle: { text: 'Great Eagle!', color: '#ffd24a', size: 17 },
  birdie: { text: 'Birdie!', color: '#7ef0a6', size: 16 },
  par: { text: 'Par', color: '#eef5ff', size: 14 },
  bogey: { text: 'Bogey..', color: '#ffb066', size: 14 },
  double: { text: 'Double Bogey…', color: '#ff7a6b', size: 14 },
};
/** How long the floating score popup stays up — a quick beat, not the full linger. */
const SCORE_POPUP_DURATION = 1.5;

/** The aim/meter line is drawn this FRACTION of the box's SET range — a shortened bar
 *  that stops well short of the hole (the power band still maps onto it). */
// The aim/meter bar runs this fraction of the way TO THE PIN (not the card's full reach),
// so it always stops short of the target green — for every card, near or far pin.
const SWING_LINE_FRAC = 0.55;

/** Confetti colours for the hole-in-one burst. */
const CONFETTI = ['#ff5a5a', '#ffd24a', '#5ad15a', '#5aa6ff', '#ff7ad1', '#ffffff', '#ff9e3d'] as const;

/** Depth (field px) of the grandstand band at the TOP and BOTTOM ends (point-symmetric). */
const STAND_DEPTH = 46;
/** Crowd shirt colours (the people in the stands). */
const SHIRTS = ['#d9544d', '#4d86d9', '#e0b23a', '#5bbf6a', '#b86fd0', '#e8e8e8', '#e08a3a', '#7a8a99'] as const;

/**
 * Read a CSS env() safe-area inset (px). Cached per resize; returns 0 when the
 * environment can't report it (e.g. the node mock context test).
 */
function safeAreaInsets(): { top: number; right: number; bottom: number; left: number } {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;visibility:hidden;pointer-events:none;' +
    'top:env(safe-area-inset-top,0px);right:env(safe-area-inset-right,0px);' +
    'bottom:env(safe-area-inset-bottom,0px);left:env(safe-area-inset-left,0px);';
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const px = (v: string): number => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const insets = { top: px(cs.top), right: px(cs.right), bottom: px(cs.bottom), left: px(cs.left) };
  probe.remove();
  return insets;
}

// ---------------------------------------------------------------------------
// Renderer factory
// ---------------------------------------------------------------------------

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('createRenderer: 2D context unavailable on the supplied canvas.');
  }
  // The real on-canvas context — used ONLY to blit the finished pixel buffer up to the screen.
  const screen = ctx;
  // `c` is the ACTIVE scene context: in the browser it's a LOW-RES offscreen "pixel buffer" (drawn
  // then nearest-neighbor upscaled → chunky 8-bit pixels); with no DOM (the headless render harness)
  // it falls back to the canvas itself at full resolution, so that path is byte-for-byte unchanged.
  let c: CanvasRenderingContext2D = ctx;
  let pixelBuffer: HTMLCanvasElement | null = null;
  let bufferCtx: CanvasRenderingContext2D | null = null;
  let bufW = 0;
  let bufH = 0;
  let sceneScale = 1; // CSS-px → scene-backing-store scale: 1/PIXEL for the buffer, else dpr.

  // Loaded pixel-art sprites (terrain tiles + characters). Null → procedural drawing (headless,
  // load failure, or before the assets finish loading).
  let sprites: SpriteSet | null = null;
  // Player handedness (a setting): left-handed flips YOUR golfer 180° so the RIGHT shoulder leads
  // to the hole instead of the left. Only affects the player's golfers, not the rival's.
  let leftHanded = false;

  // --- letterbox transform state (recomputed in resize) ---
  const view: Viewport = { width: 0, height: 0, dpr: 1 };
  let scale = 1; // field-space px → CSS px
  let offsetX = 0; // CSS-px left margin of the letterboxed field
  let offsetY = 0; // CSS-px top margin of the letterboxed field

  // --- cached, layout-dependent gradients (rebuilt only on resize, §7) ---
  // These depend solely on the viewport/letterbox, never on per-frame state, so
  // they are built once per resize and reused every frame (no hot-path alloc).
  let frameGlow: CanvasGradient | null = null;
  // Cached ROUGH-GRASS turf. A DENSE blade texture is painted once into an offscreen
  // canvas on resize (thousands of tapered, shaded blades) and blitted every frame;
  // it keeps the two play areas' light-top→dark-bottom cross-fade. A gradient + sparse
  // marks is the fallback when there is no DOM (the mock-canvas render harness).
  let turfTexture: HTMLCanvasElement | null = null;
  // Decorative trees framing the play area (FIELD-space; resize-independent). Placed
  // in the outer rough, clear of greens/tees/fairways. Rebuilt when the layout changes.
  let trees: { x: number; y: number; r: number; seed: number }[] = [];
  let treesSig = '';
  // Crowd in the grandstands (FIELD-space dots, built once). They light up + bob on a cheer.
  let standDots: { x: number; y: number; r: number; color: string; phase: number }[] = [];
  function readViewport(): Viewport {
    const rect = canvas.getBoundingClientRect();
    // Fall back to attribute width/height when the element isn't laid out (tests).
    const cssW = rect.width || canvas.width || FIELD.width;
    const cssH = rect.height || canvas.height || FIELD.height;
    const dpr =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? Math.min(window.devicePixelRatio, 2) // cap at 2 (prototype) — §7 fill-rate budget
        : 1;
    return { width: cssW, height: cssH, dpr };
  }

  function resize(): void {
    const v = readViewport();
    (view as { width: number; height: number; dpr: number }).width = v.width;
    (view as { width: number; height: number; dpr: number }).height = v.height;
    (view as { width: number; height: number; dpr: number }).dpr = v.dpr;

    // HiDPI backing store sized to CSS px × dpr.
    canvas.width = Math.max(1, Math.round(v.width * v.dpr));
    canvas.height = Math.max(1, Math.round(v.height * v.dpr));

    // Letterbox the 440×900 field into the safe-area-reduced viewport, centred.
    const insets = safeAreaInsets();
    const availW = Math.max(1, v.width - insets.left - insets.right);
    const availH = Math.max(1, v.height - insets.top - insets.bottom);
    scale = Math.min(availW / FIELD.width, availH / FIELD.height);
    const drawnW = FIELD.width * scale;
    const drawnH = FIELD.height * scale;
    offsetX = insets.left + (availW - drawnW) / 2;
    offsetY = insets.top + (availH - drawnH) / 2;

    // --- 8-bit pixel buffer: size an offscreen canvas to 1/PIXEL of the viewport and point the
    // scene context `c` at it (browser only). All draw code keeps working in CSS-px — the buffer's
    // transform (sceneScale = 1/PIXEL) just renders it at low resolution; present() upscales it
    // crisp. Cached gradients are (re)built on `c` below, so this MUST run before buildTurfStripes().
    if (USE_PIXEL_BUFFER && typeof document !== 'undefined') {
      bufW = Math.max(1, Math.ceil(v.width / PIXEL));
      bufH = Math.max(1, Math.ceil(v.height / PIXEL));
      pixelBuffer ??= document.createElement('canvas');
      pixelBuffer.width = bufW;
      pixelBuffer.height = bufH;
      bufferCtx = pixelBuffer.getContext('2d');
      if (bufferCtx !== null) {
        bufferCtx.imageSmoothingEnabled = false; // crisp downscale of image sources (the turf)
        c = bufferCtx;
        sceneScale = 1 / PIXEL;
      } else {
        c = screen;
        sceneScale = v.dpr;
      }
    } else {
      // Headless (render harness): no offscreen canvas — draw straight to the screen, as before.
      pixelBuffer = null;
      bufferCtx = null;
      c = screen;
      sceneScale = v.dpr;
    }

    // Turf fills the FULL column height (de-boxed: the play field uses the top/
    // bottom room instead of leaving dusk margins). Greens/tees keep their
    // letterboxed positions, so shot geometry (and §5b) is unchanged. The two play
    // areas cross-fade smoothly across the midline (no hard seam).
    buildTurfStripes();

    // Frame glow depends only on the viewport.
    const gx = v.width / 2;
    const gy = -v.height * 0.1;
    frameGlow = c.createRadialGradient(gx, gy, 0, gx, gy, v.width * 1.2);
    frameGlow.addColorStop(0, TOKENS.dusk2); // §8 --dusk-2
    frameGlow.addColorStop(1, 'rgba(0,0,0,0)');
  }

  // --- coordinate transforms (used by input via main; allocation-free internals) ---

  function fieldToScreen(p: Vec2): { x: number; y: number } {
    screenOut.x = offsetX + p.x * scale;
    screenOut.y = offsetY + p.y * scale;
    return screenOut;
  }

  function screenToField(clientX: number, clientY: number): Vec2 {
    const rect = canvas.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    scratchField.x = (localX - offsetX) / scale;
    scratchField.y = (localY - offsetY) / scale;
    return scratchField;
  }

  // --- internal helpers that write field-space → CSS-px into scratchScreen ---

  function sx(fx: number): number {
    return offsetX + fx * scale;
  }
  function sy(fy: number): number {
    return offsetY + fy * scale;
  }
  /** Scale a field-space length to CSS px. */
  function sl(len: number): number {
    return len * scale;
  }

  /** Tile a sprite across the rect [x0,y0]→[x1,y1] at `tile` CSS-px squares. The caller owns any
   *  clip (e.g. to a green/fairway outline). Smoothing is already off, so each tile is crisp. */
  function tileRect(img: HTMLImageElement, x0: number, y0: number, x1: number, y1: number, tile: number): void {
    for (let yy = y0; yy < y1; yy += tile) {
      for (let xx = x0; xx < x1; xx += tile) {
        c.drawImage(img, xx, yy, tile, tile);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Draw passes — order per the spec: frame → turf → greens → tees → hazards
  // (hazards are drawn inside the green pass, clipped) → balls+trails → wind →
  // swing meter + aim line.
  // -------------------------------------------------------------------------

  function drawFrame(): void {
    // Dusk-navy frame fills the whole backing store; the field letterboxes on top.
    c.fillStyle = TOKENS.dusk; // §8 --dusk
    c.fillRect(0, 0, view.width, view.height);
    // Subtle upper glow (mirrors the prototype #app radial-gradient), cached on resize.
    if (frameGlow !== null) {
      c.fillStyle = frameGlow;
      c.fillRect(0, 0, view.width, view.height);
    }
  }

  /**
   * Precompute the PIXEL rough-grass turf (resize-stable): {@link paintGrass} renders flat tufted
   * 8-bit grass into an offscreen canvas at the BUFFER's resolution (so each tuft is a crisp 1px
   * speck, never a downscaled blur). Two flat tone zones — rival half (top) over your half
   * (bottom) — meet at the midline with a dithered seam. Browser only; with no DOM (the render
   * harness) `turfTexture` stays null and drawTurf paints a flat fallback.
   */
  function buildTurfStripes(): void {
    const split = offsetY + (FIELD.height / 2) * scale; // midline, CSS px
    turfTexture = null;
    if (typeof document === 'undefined') return;
    const tw = Math.max(1, Math.round(sl(FIELD.width) / PIXEL));
    const th = Math.max(1, Math.round(view.height / PIXEL));
    const splitY = Math.max(0, Math.min(th, Math.round(split / PIXEL)));
    const off = document.createElement('canvas');
    off.width = tw;
    off.height = th;
    const og = off.getContext('2d');
    if (og === null) return;
    paintGrass(og, tw, th, splitY);
    turfTexture = off;
  }

  function drawTurf(): void {
    const x = offsetX;
    const w = sl(FIELD.width);
    // Clip to the turf rect so it never bleeds outside the field width.
    c.save();
    c.beginPath();
    c.rect(x, 0, w, view.height);
    c.clip();
    if (sprites !== null) {
      tileRect(sprites.grass, x, 0, x + w, view.height, sl(SPRITE.tileRough)); // real grass tiles
    } else if (turfTexture !== null) {
      c.drawImage(turfTexture, x, 0, w, view.height); // crisp upscale (buffer smoothing is off)
    } else {
      c.fillStyle = THEME.you.turfA; // No-DOM fallback (render harness): a flat green.
      c.fillRect(x, 0, w, view.height);
    }
    c.restore();
  }

  function drawGreenLabels(state: GameState): void {
    c.fillStyle = 'rgba(255,255,255,0.30)';
    c.font = `${sl(9)}px Inter, system-ui, sans-serif`;
    c.textAlign = 'center';
    const rg = state.rival.green;
    const yg = state.you.green;
    c.fillText('RIVAL — attack', sx(rg.center.x), sy(rg.center.y + rg.radius + 16));
    c.fillText('YOURS — defend', sx(yg.center.x), sy(yg.center.y - yg.radius - 10));
  }

  /**
   * Trace a green outline buffer (already sampled in FIELD space) as a closed
   * canvas path in screen space. Caller fills / clips. No allocation.
   */
  function traceOutline(outline: { x: number; y: number }[]): void {
    c.beginPath();
    const first = outline[0] as { x: number; y: number };
    c.moveTo(sx(first.x), sy(first.y));
    for (let i = 1; i < outline.length; i++) {
      const p = outline[i] as { x: number; y: number };
      c.lineTo(sx(p.x), sy(p.y));
    }
    c.closePath();
  }

  /** Draw one hazard (sand / water) on the open turf, tracing its PROCEDURAL outline
   *  (the SAME ellipse+lobe shape as greens/fairways — what you see is what scores).
   *  Hazards live in the ROUGH (never on a surface), so they are NOT clipped. */
  function drawHazard(hz: Hazard): void {
    // Trees are fly-over OBSTACLES drawn ABOVE the greens (with the decorative trees), not a
    // rough catch-disc painted under them — skip here, rendered in drawTrees().
    if (hz.type === 'tree') return;
    const hx = sx(hz.pos.x);
    const hy = sy(hz.pos.y);
    const hr = sl(hz.radius);
    // Fill the scratch region from the hazard (circle fallback if it has no shape).
    hazardShape.center.x = hz.pos.x;
    hazardShape.center.y = hz.pos.y;
    hazardShape.radius = hz.radius;
    hazardShape.rx = hz.rx ?? hz.radius;
    hazardShape.ry = hz.ry ?? hz.radius;
    hazardShape.rot = hz.rot ?? 0;
    hazardShape.lobe = hz.lobe ?? 0;
    hazardShape.lobePhase = hz.lobePhase ?? 0;
    sampleGreenOutline(hazardShape, SHAPE.outlineSegments, surfaceOutline, 1);

    if (sprites !== null) {
      // tiled water/sand sprite, clipped to the hazard shape, with a thin rim for definition
      c.save();
      traceOutline(surfaceOutline);
      c.clip();
      tileRect(hz.type === 'sand' ? sprites.sand : sprites.water, hx - hr * 1.5, hy - hr * 1.5, hx + hr * 1.5, hy + hr * 1.5, sl(SPRITE.tileHazard));
      c.restore();
      c.strokeStyle = hz.type === 'sand' ? TOKENS.sandDark : TOKENS.waterDark;
      c.lineWidth = sl(1.5);
      traceOutline(surfaceOutline);
      c.stroke();
      return;
    }

    if (hz.type === 'sand') {
      c.fillStyle = '#e3cf94'; // §8 --sand family
      traceOutline(surfaceOutline);
      c.fill();
      // Thin rim so the bunker reads against the dark rough.
      c.strokeStyle = TOKENS.sandDark; // §8 --sand-dark
      c.lineWidth = sl(1.5);
      traceOutline(surfaceOutline);
      c.stroke();
      // Inner shadow (outline scaled in) for a little depth.
      c.fillStyle = 'rgba(0,0,0,0.10)';
      sampleGreenOutline(hazardShape, SHAPE.outlineSegments, surfaceOutline, 0.7);
      traceOutline(surfaceOutline);
      c.fill();
    } else {
      const wg = c.createRadialGradient(hx, hy, sl(2), hx, hy, hr);
      wg.addColorStop(0, TOKENS.water); // §8 --water
      wg.addColorStop(1, TOKENS.waterDark); // §8 --water-dark
      c.fillStyle = wg;
      traceOutline(surfaceOutline);
      c.fill();
    }
  }

  /** Draw both sides' hazards (rough discs). Painted before fairways/greens so the
   *  manicured surfaces always sit cleanly on top of any sliver that kisses an edge. */
  function drawHazards(state: GameState): void {
    for (const hz of state.you.green.hazards) drawHazard(hz);
    for (const hz of state.rival.green.hazards) drawHazard(hz);
  }

  /**
   * Place decorative trees framing the play area (FIELD space, deterministic). Trees
   * sit in the OUTER rough — within `band` px of a field edge — and are rejected near
   * any green, tee or fairway so they never sit on a manicured/play surface. Rebuilt
   * only when the layout (green centres) changes.
   */
  function buildTrees(state: GameState): void {
    const sig = `${state.you.green.center.x},${state.you.green.center.y};${state.rival.green.center.x},${state.rival.green.center.y}`;
    if (sig === treesSig && trees.length > 0) return;
    treesSig = sig;

    const W = FIELD.width;
    const H = FIELD.height;
    const band = 74; // depth of the tree frame from each edge (field px)
    const hash = (n: number): number => {
      const s = Math.sin(n * 41.17 + 7.3) * 9123.43;
      return s - Math.floor(s);
    };
    // Avoid zones: greens (+ collar margin) and each side's launch point.
    const avoid: { x: number; y: number; r: number }[] = [];
    for (const side of [state.you, state.rival]) {
      avoid.push({ x: side.green.center.x, y: side.green.center.y, r: side.green.radius + 58 });
      avoid.push({ x: side.fairway.center.x, y: side.fairway.center.y, r: side.fairway.radius + 30 });
      for (const key of ['long', 'mid', 'short'] as const) {
        avoid.push({ x: side.tees[key].pos.x, y: side.tees[key].pos.y, r: 72 }); // keep trees off the golfers
      }
    }

    const out: { x: number; y: number; r: number; seed: number }[] = [];
    const spacing = 50;
    let i = 1;
    for (let gy = band * 0.4; gy < H; gy += spacing) {
      for (let gx = band * 0.4; gx < W; gx += spacing) {
        const x = gx + (hash(i * 1.3) - 0.5) * spacing * 0.8;
        const y = gy + (hash(i * 2.7) - 0.5) * spacing * 0.8;
        i += 1;
        if (x < 6 || x > W - 6 || y < 6 || y > H - 6) continue;
        // keep only the outer frame.
        const nearEdge = x < band || x > W - band || y < band || y > H - band;
        if (!nearEdge) continue;
        let ok = true;
        for (const a of avoid) {
          if (Math.hypot(x - a.x, y - a.y) < a.r) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;
        out.push({ x, y, r: 17 + hash(i * 3.9) * 13, seed: Math.floor(hash(i * 5.1) * 1000) });
      }
    }
    trees = out;
  }

  /** A top-down tree: the pixel-art sprite (mirrored per-seed for variety), or a procedural canopy. */
  function drawTree(cx: number, cy: number, r: number, seed: number): void {
    if (sprites !== null) {
      const variants = sprites.trees;
      const img = variants[Math.abs(seed) % variants.length] ?? variants[0];
      if (img) {
        const aspect = img.width / Math.max(1, img.height);
        const hh = r * SPRITE.treeScale; // half-height
        const hw = hh * aspect; // half-width (preserve the variant's shape)
        c.save();
        c.translate(cx, cy);
        c.scale(seed & 1 ? -1 : 1, 1); // flip some horizontally so they aren't all identical
        c.drawImage(img, -hw, -hh, hw * 2, hh * 2);
        c.restore();
        return;
      }
    }
    const hash = (n: number): number => {
      const s = Math.sin((seed + n) * 91.7 + 13.1) * 4137.27;
      return s - Math.floor(s);
    };
    // soft round shadow (down-right)
    c.fillStyle = 'rgba(16,34,14,0.2)';
    c.beginPath();
    c.arc(cx + r * 0.34, cy + r * 0.46, r * 0.9, 0, TWO_PI);
    c.fill();
    // canopy: overlapping lobes give a bumpy silhouette (single fill, no inner seams)
    c.fillStyle = '#2d7a3a';
    const lobes = 6;
    for (let j = 0; j < lobes; j++) {
      const a = (j / lobes) * TWO_PI + hash(j) * 0.5;
      const d = r * (0.42 + hash(j + 10) * 0.22);
      c.beginPath();
      c.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d, r * (0.46 + hash(j + 3) * 0.16), 0, TWO_PI);
      c.fill();
    }
    c.beginPath();
    c.arc(cx, cy, r * 0.66, 0, TWO_PI);
    c.fill();
    // upper-left highlight for a hint of volume
    c.fillStyle = '#56b056';
    for (let j = 0; j < 3; j++) {
      c.beginPath();
      c.arc(cx - r * 0.22 + j * r * 0.22, cy - r * 0.26 - hash(j + 7) * r * 0.1, r * 0.28, 0, TWO_PI);
      c.fill();
    }
  }

  function drawTrees(state: GameState): void {
    buildTrees(state);
    for (const t of trees) drawTree(sx(t.x), sy(t.y), sl(t.r), t.seed);
    // Gameplay TREE hazards (a defensive card): a real obstacle in front of a green that blocks
    // low/straight shots. Drawn bigger than the decor + with a faint block-zone ring so the
    // player can read the line they must fly high / shape around.
    for (const side of [state.you.green, state.rival.green]) {
      for (const hz of side.hazards) {
        if (hz.type !== 'tree') continue;
        const cx = sx(hz.pos.x);
        const cy = sy(hz.pos.y);
        c.fillStyle = 'rgba(16,34,14,0.16)'; // soft block-zone footprint
        c.beginPath();
        c.arc(cx, cy, sl(TREE.blockRadius), 0, TWO_PI);
        c.fill();
        drawTree(cx, cy, sl(Math.max(hz.radius, TREE.blockRadius * 0.8)), Math.floor(hz.pos.x * 7.3 + hz.pos.y * 3.1));
      }
    }
  }

  /** Draw a green: procedural SHAPE surface + collar, slope shading + break arrows
   *  (clipped to the shape), scoring rings (circles), hazards (clipped), pin. */
  function drawGreen(green: Green, mine: boolean): void {
    const cx = sx(green.center.x);
    const cy = sy(green.center.y);
    const r = sl(green.radius);

    // Sample the procedural outline (field space) once: surface + expanded collar.
    // The collar is the surface outline scaled out by COLLAR_PX about the centre —
    // the SAME shared sampler scoring uses, so the painted edge == the scored edge.
    const collarScale = (green.radius + COLLAR_PX) / green.radius;
    sampleGreenOutline(green, SHAPE.outlineSegments, surfaceOutline, 1);
    sampleGreenOutline(green, SHAPE.outlineSegments, collarOutline, collarScale);

    const theme = mine ? THEME.you : THEME.rival;

    // collar (shape outline, expanded)
    c.fillStyle = theme.collar;
    traceOutline(collarOutline);
    c.fill();

    // surface (shape outline) — tiled putting-green sprite, or the flat alt-green fallback
    if (sprites !== null) {
      c.save();
      traceOutline(surfaceOutline);
      c.clip();
      tileRect(sprites.green, cx - r * 1.5, cy - r * 1.5, cx + r * 1.5, cy + r * 1.5, sl(SPRITE.tileGreen));
      c.restore();
    } else {
      c.fillStyle = theme.feature;
      traceOutline(surfaceOutline);
      c.fill();
    }

    // --- slope shading (high side light, downhill dark) + break arrows, clipped ---
    const sxComp = Math.cos(green.slopeAngle) * green.slopeMag;
    const syComp = Math.sin(green.slopeAngle) * green.slopeMag;

    c.save();
    traceOutline(surfaceOutline);
    c.clip();

    // Pixel slope read: a flat, hard-edged darker tone over the DOWNHILL half (no gradient) — the
    // low side the ball breaks toward, plus a brighter sliver on the uphill edge. Rotate so downhill
    // = +x, then cover each half.
    const slopeAng = Math.atan2(syComp, sxComp);
    c.save();
    c.translate(cx, cy);
    c.rotate(slopeAng);
    c.fillStyle = `rgba(255,255,255,${0.06 + green.slopeMag * 0.05})`; // uphill highlight
    c.fillRect(-r * 1.4, -r * 1.3, r * 1.4, r * 2.6);
    c.fillStyle = `rgba(10,26,16,${0.12 + green.slopeMag * 0.08})`; // downhill shade
    c.fillRect(0, -r * 1.3, r * 1.4, r * 2.6);
    c.restore();

    // directional break arrows pointing downhill (each with a small drop shadow so it
    // pops off the turf and the direction is unmistakable)
    const ang = Math.atan2(syComp, sxComp);
    const arrLen = sl(9);
    const head = sl(4);
    const sh = sl(1); // shadow offset (down-right)
    const drawArrows = (dx: number, dy: number, stroke: string, fill: string): void => {
      c.strokeStyle = stroke;
      c.fillStyle = fill;
      c.lineWidth = sl(1.5);
      for (let ay = -1; ay <= 1; ay++) {
        for (let ax = -1; ax <= 1; ax++) {
          const ox = cx + ax * r * 0.5 + dx;
          const oy = cy + ay * r * 0.5 + dy;
          if (Math.hypot(ox - dx - cx, oy - dy - cy) > r * 0.8) continue;
          const ex = ox + Math.cos(ang) * arrLen;
          const ey = oy + Math.sin(ang) * arrLen;
          c.beginPath();
          c.moveTo(ox, oy);
          c.lineTo(ex, ey);
          c.stroke();
          c.beginPath();
          c.moveTo(ex, ey);
          c.lineTo(ex - Math.cos(ang - 0.5) * head, ey - Math.sin(ang - 0.5) * head);
          c.lineTo(ex - Math.cos(ang + 0.5) * head, ey - Math.sin(ang + 0.5) * head);
          c.fill();
        }
      }
    };
    drawArrows(sh, sh, 'rgba(0,0,0,0.35)', 'rgba(0,0,0,0.35)'); // shadow pass
    drawArrows(0, 0, 'rgba(255,255,255,0.55)', 'rgba(255,255,255,0.55)'); // arrows
    c.restore();

    // --- scoring rings around the pin (birdie + eagle), CIRCLES, container-relative.
    // These track the actual SCORING factors so what you see is what scores. ---
    const px = sx(green.pin.x);
    const py = sy(green.pin.y);
    c.setLineDash([sl(3), sl(4)]);
    c.strokeStyle = 'rgba(124,196,127,0.45)'; // birdie ring (fairway-ish)
    c.lineWidth = sl(1.5);
    c.beginPath();
    c.arc(px, py, r * SCORING.birdieRadiusFactor, 0, TWO_PI);
    c.stroke();
    c.strokeStyle = 'rgba(242,193,78,0.6)'; // eagle ring (gold)
    c.beginPath();
    c.arc(px, py, r * SCORING.eagleRadiusFactor, 0, TWO_PI);
    c.stroke();
    c.setLineDash([]);

    // Hazards are no longer drawn here: they live in the ROUGH around the green/
    // fairway (never on the surface), painted by drawHazards() before the greens.

    // --- cup + pin/flag ---
    c.fillStyle = '#0c2a1c'; // the cup (hole)
    c.beginPath();
    c.arc(px, py, sl(6), 0, TWO_PI);
    c.fill();
    if (sprites !== null) {
      // pin sprite (gold = yours, red = rival), pole base anchored at the cup
      const img = mine ? sprites.flagGold : sprites.flagRed;
      const dh = sl(SPRITE.flagH);
      const dw = dh * (img.width / Math.max(1, img.height));
      c.drawImage(img, px - SPRITE.flagPoleFracX * dw, py - SPRITE.flagPoleFracY * dh, dw, dh);
    } else {
      c.strokeStyle = '#ffffff';
      c.lineWidth = sl(2);
      c.beginPath();
      c.moveTo(px, py);
      c.lineTo(px, py - sl(32));
      c.stroke();
      c.fillStyle = mine ? TOKENS.gold : TOKENS.flag; // §8 your flag gold, rival flag red
      c.beginPath();
      c.moveTo(px, py - sl(32));
      c.lineTo(px + sl(13), py - sl(27));
      c.lineTo(px, py - sl(22));
      c.fill();
    }
  }

  /** Draw a fairway: the same procedural SHAPE outline as a green (surface + collar) in
   *  fairway tones with faint mowing stripes — the strip the mid/short shots launch from. */
  function drawFairway(fairway: Fairway, mine: boolean): void {
    const theme = mine ? THEME.you : THEME.rival;
    const collarScale = (fairway.radius + COLLAR_PX) / fairway.radius;
    sampleGreenOutline(fairway, SHAPE.outlineSegments, surfaceOutline, 1);
    sampleGreenOutline(fairway, SHAPE.outlineSegments, collarOutline, collarScale);

    c.fillStyle = theme.collar;
    traceOutline(collarOutline);
    c.fill();

    const fcx = sx(fairway.center.x);
    const fcy = sy(fairway.center.y);
    const r = sl(fairway.radius);

    if (sprites !== null) {
      // tiled fairway sprite (mow stripes are baked into the art)
      c.save();
      traceOutline(surfaceOutline);
      c.clip();
      tileRect(sprites.fairway, fcx - r * 1.6, fcy - r * 1.6, fcx + r * 1.6, fcy + r * 1.6, sl(SPRITE.tileFairway));
      c.restore();
      return;
    }

    // procedural fallback: flat fill + faint mowing stripes
    c.fillStyle = theme.feature;
    traceOutline(surfaceOutline);
    c.fill();

    c.save();
    traceOutline(surfaceOutline);
    c.clip();
    const step = sl(9);
    let i = 0;
    for (let yy = fcy - r; yy < fcy + r; yy += step, i++) {
      c.globalAlpha = 0.08;
      c.fillStyle = i % 2 ? '#ffffff' : '#000000';
      c.fillRect(fcx - r, yy, r * 2, step);
    }
    c.globalAlpha = 1;
    c.restore();
  }

  /**
   * Rough patch (the Rough defensive card, §3.7): a small tuft of unmowed, darker grass right on
   * the roughed TEE (the per-tee debuff spot). It appears when the card lands and eases out as the
   * timer runs down. Driven purely by the tee's `roughRemaining` — it clears when the debuff does.
   */
  function drawRoughPatch(pos: Vec2, roughRemaining: number): void {
    if (roughRemaining <= 0) return;
    const alpha = Math.min(1, roughRemaining / 0.8); // ease out over the last 0.8s before it clears
    const cx = sx(pos.x);
    const cy = sy(pos.y);
    const r = sl(26); // patch sitting on the roughed launch spot (sized to read under the golfer)
    const seed = Math.floor(pos.x * 5.1 + pos.y * 3.7);
    const hash = (n: number): number => {
      const s = Math.sin((seed + n) * 45.2 + 7.3) * 4193.1;
      return s - Math.floor(s);
    };

    c.save();
    c.globalAlpha = alpha;

    // Dark, irregular rough base (overlapping lobes for an organic blob).
    c.fillStyle = '#2c5e34';
    const lobes = 7;
    for (let j = 0; j < lobes; j++) {
      const a = (j / lobes) * TWO_PI + hash(j) * 0.6;
      const d = r * (0.3 + hash(j + 10) * 0.4);
      c.beginPath();
      c.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d, r * (0.4 + hash(j + 3) * 0.25), 0, TWO_PI);
      c.fill();
    }
    c.beginPath();
    c.arc(cx, cy, r * 0.6, 0, TWO_PI);
    c.fill();

    // Unmowed blades sticking up (short strokes, dark + light) for the "rough" read.
    c.lineCap = 'round';
    for (let i = 0; i < 18; i++) {
      const a = hash(i * 1.7) * TWO_PI;
      const rad = hash(i * 2.3) * r * 0.95;
      const bx = cx + Math.cos(a) * rad;
      const by = cy + Math.sin(a) * rad;
      const len = sl(3 + hash(i * 3.1) * 3.5);
      const lean = (hash(i * 4.7) - 0.5) * 0.8;
      c.strokeStyle = hash(i * 5.3) < 0.5 ? 'rgba(20,52,24,0.9)' : 'rgba(110,165,80,0.85)';
      c.lineWidth = Math.max(1, sl(1.1));
      c.beginPath();
      c.moveTo(bx, by);
      c.lineTo(bx + Math.sin(lean) * len, by - Math.cos(lean) * len);
      c.stroke();
    }
    c.restore();
  }

  /** A little golfer standing on a tee, addressing the ball toward the target pin (the figure is
   *  a billboard like the flag — side-coloured shirt/cap, a club to the ground in front). */
  function drawGolfer(pos: Vec2, targetPin: Vec2, mine: boolean): void {
    const x = sx(pos.x);
    const y = sy(pos.y);
    const u = sl(2); // figure unit (doubled for a bigger, more readable golfer)
    const face = targetPin.x >= pos.x ? 1 : -1; // face toward the target horizontally
    const shirt = mine ? '#eef2f7' : '#e0463a';

    // feet shadow
    c.fillStyle = 'rgba(0,0,0,0.22)';
    c.beginPath();
    c.ellipse(x, y + u * 0.6, u * 4, u * 1.6, 0, 0, TWO_PI);
    c.fill();
    // legs
    c.strokeStyle = '#2b2f3a';
    c.lineCap = 'round';
    c.lineWidth = u * 1.5;
    c.beginPath();
    c.moveTo(x - u, y);
    c.lineTo(x - u * 0.5, y - u * 4);
    c.moveTo(x + u, y);
    c.lineTo(x + u * 0.5, y - u * 4);
    c.stroke();
    // torso (shirt)
    c.fillStyle = shirt;
    c.beginPath();
    c.ellipse(x, y - u * 6, u * 2.3, u * 3, 0, 0, TWO_PI);
    c.fill();
    // arm + club to the ground in front
    const hx = x + face * u * 1.6;
    const hy = y - u * 6.2;
    c.strokeStyle = shirt;
    c.lineWidth = u * 1.2;
    c.beginPath();
    c.moveTo(x, y - u * 6.6);
    c.lineTo(hx, hy);
    c.stroke();
    c.strokeStyle = 'rgba(235,235,235,0.92)';
    c.lineWidth = Math.max(1, u * 0.8);
    c.beginPath();
    c.moveTo(hx, hy);
    c.lineTo(x + face * u * 5, y - u * 0.5);
    c.stroke();
    // head + cap
    c.fillStyle = '#e7b98c';
    c.beginPath();
    c.arc(x, y - u * 9.6, u * 1.9, 0, TWO_PI);
    c.fill();
    c.fillStyle = mine ? '#3270b3' : '#8a1f1a';
    c.beginPath();
    c.arc(x, y - u * 9.8, u * 2, Math.PI, TWO_PI); // cap dome (top half)
    c.fill();
    c.fillRect(face > 0 ? x : x - u * 2.4, y - u * 10.2, u * 2.4, u * 0.8); // brim toward the target
    c.lineCap = 'butt';
  }

  /** A mowed tee BOX, in the side's tee tones — drawn only under the LONG golfer (which stands
   *  back in the rough, off the fairway), so it reads as a proper teeing ground. */
  function drawTeeBox(pos: Vec2, mine: boolean): void {
    const x = sx(pos.x);
    const y = sy(pos.y);
    if (sprites !== null) {
      const img = sprites.teePad;
      const w = sl(SPRITE.teePadW);
      const hh = w * (img.height / Math.max(1, img.width));
      c.drawImage(img, x - w / 2, y - hh / 2, w, hh);
      return;
    }
    const theme = mine ? THEME.you : THEME.rival;
    const w = sl(38);
    const h = sl(24);
    c.fillStyle = theme.teeBot; // a darker lip peeking below for a little depth
    c.fillRect(x - w / 2, y - h / 2 + sl(2), w, h);
    c.fillStyle = theme.teeTop;
    c.fillRect(x - w / 2, y - h / 2, w, h);
  }

  /** Draw a pixel-art golfer sprite at a tee, anchored with the ball at the tee. The sprite faces
   *  toward its ball (down-field); mirror it vertically when the target is UP-field so it always
   *  addresses the right way. */
  function drawGolferSprite(img: HTMLImageElement, pos: Vec2, targetPin: Vec2, flip: boolean): void {
    const x = sx(pos.x);
    const y = sy(pos.y);
    const h = sl(SPRITE.golferFieldH);
    const w = h * (img.width / Math.max(1, img.height));
    // Proper golf address: the golfer's SHOULDER line (the sprite's horizontal/x axis) must run in
    // line with the hole. So rotate the sprite's +x axis onto the tee→pin direction (a real stance
    // is side-on: shoulders parallel to the target line). `flip` adds 180° for a left-handed player
    // (right shoulder leads). The ball stays anchored at the tee.
    const ang = Math.atan2(sy(targetPin.y) - y, sx(targetPin.x) - x) + (flip ? Math.PI : 0);
    c.save();
    c.translate(x, y);
    c.rotate(ang);
    c.drawImage(img, -w / 2, -h, w, h);
    c.restore();
  }

  /** Draw one tee spot: a golfer addressing the target (the golfer IS the spot marker — the
   *  mowed pads were dropped). The LONG tee adds a tee box under the golfer; a fouled tee shows
   *  the rough tuft. */
  function drawTeeSpot(tee: TeeBox, targetPin: Vec2, mine: boolean): void {
    if (tee.key === 'long') drawTeeBox(tee.pos, mine);
    if (tee.roughRemaining > 0) drawRoughPatch(tee.pos, tee.roughRemaining);
    if (sprites !== null) drawGolferSprite(mine ? sprites.golferBlue : sprites.golferRed, tee.pos, targetPin, mine && leftHanded);
    else drawGolfer(tee.pos, targetPin, mine);
  }

  /** Every tee-off spot for both sides (long/mid/short): a tee pad + golfer, rough tuft when fouled. */
  function drawTeeSpots(state: GameState): void {
    for (const key of TEE_KEYS) {
      drawTeeSpot(state.you.tees[key], state.rival.green.pin, true);
      drawTeeSpot(state.rival.tees[key], state.you.green.pin, false);
    }
  }

  /** A ball that came to rest in WATER: an expanding splash ring + droplets, then
   *  the ball shrinks, darkens and fades as it sinks under the surface. */
  function drawSink(ball: Ball): void {
    const cx = sx(ball.pos.x);
    const cy = sy(ball.pos.y);
    const r0 = sl(6);
    const t = Math.min(1, Math.max(0, 1 - ball.restRemaining / BALL_SINK_SECONDS)); // 0 → 1
    const rival = ball.side === 'rival';

    // Splash: expanding rings + a spray of droplets, fading over the first ~55%.
    const splash = Math.max(0, 1 - t / 0.55);
    if (splash > 0) {
      c.strokeStyle = `rgba(232,246,255,${0.6 * splash})`;
      c.lineWidth = sl(1.6);
      for (let k = 0; k < 2; k++) {
        c.beginPath();
        c.arc(cx, cy, r0 * (1 + (1.6 + k * 1.4) * t * 1.8), 0, TWO_PI);
        c.stroke();
      }
      c.fillStyle = `rgba(214,238,255,${0.75 * splash})`;
      const spray = r0 * (1 + 2.4 * t);
      const rise = sl(5) * Math.sin(Math.min(1, t * 2) * Math.PI); // droplets arc up then down
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * TWO_PI;
        c.beginPath();
        c.arc(cx + Math.cos(a) * spray, cy + Math.sin(a) * spray - rise, sl(1.3), 0, TWO_PI);
        c.fill();
      }
    }

    // Sinking ball: shrinks + sinks a touch + fades, tinting toward the water.
    const rr = r0 * (1 - 0.8 * t);
    if (rr > 0.4) {
      c.globalAlpha = 1 - t * 0.85;
      c.fillStyle = rival ? '#ffd2cd' : '#ffffff';
      c.beginPath();
      c.arc(cx, cy + sl(2) * t, rr, 0, TWO_PI);
      c.fill();
      // a bluish cap as it slips under
      c.fillStyle = `rgba(31,93,150,${0.5 * t})`;
      c.beginPath();
      c.arc(cx, cy + sl(2) * t, rr, 0, TWO_PI);
      c.fill();
      c.globalAlpha = 1;
    }
  }

  /** Hole-in-one: the ball settles on the lip, then drops INTO the cup (shrinks + sinks)
   *  over the first ~0.4s. After that it is in the hole and nothing is drawn. */
  function drawAceDrop(pin: Vec2, elapsed: number): void {
    const drop = (elapsed - 0.12) / 0.28; // 0..1 over 0.12..0.40s
    if (drop >= 1) return; // ball is in the hole
    const ease = drop > 0 ? drop * drop : 0;
    const r = sl(6) * (1 - 0.95 * ease);
    if (r < 0.4) return;
    const cx = sx(pin.x);
    const cy = sy(pin.y) + sl(5) * ease; // sinks into the cup
    c.fillStyle = '#ffffff';
    c.beginPath();
    c.arc(cx, cy, r, 0, TWO_PI);
    c.fill();
    c.fillStyle = `rgba(8,30,18,${0.4 * ease})`; // darkens as it drops below the lip
    c.beginPath();
    c.arc(cx, cy, r, 0, TWO_PI);
    c.fill();
  }

  /** Hole-in-one celebration: confetti erupts OUT of the cup, arcs up + out under
   *  gravity, flutters (rotating rects) and fades. Deterministic (seeded by the pin). */
  function drawConfetti(pin: Vec2, elapsed: number): void {
    const t = elapsed - 0.26; // erupts just as the ball drops in
    const DUR = 1.7;
    if (t < 0 || t > DUR) return;
    const cx = sx(pin.x);
    const cy = sy(pin.y);
    const seed = Math.floor(pin.x * 7.3 + pin.y * 3.1);
    const hash = (n: number): number => {
      const s = Math.sin((seed + n) * 12.9898) * 43758.5453;
      return s - Math.floor(s);
    };
    const grav = sl(430); // px/s²
    for (let i = 0; i < 46; i++) {
      const ang = -Math.PI / 2 + (hash(i) - 0.5) * 1.8; // mostly up, fanned
      const speed = sl(180 + hash(i + 100) * 220);
      const x = cx + Math.cos(ang) * speed * t;
      const y = cy + Math.sin(ang) * speed * t + 0.5 * grav * t * t;
      const alpha = Math.min(1, t / 0.08) * Math.min(1, (1 - t / DUR) * 1.8);
      if (alpha <= 0) continue;
      const w = sl(1.8 + hash(i + 400) * 0.9); // half-width
      c.save();
      c.globalAlpha = alpha;
      c.translate(x, y);
      c.rotate((hash(i + 200) - 0.5) * 13 * t + hash(i + 300) * TWO_PI);
      c.fillStyle = CONFETTI[i % CONFETTI.length] ?? '#ffffff';
      c.fillRect(-w, -w * 1.7, w * 2, w * 3.4);
      c.restore();
    }
    c.globalAlpha = 1;
  }

  /** Build the crowd once (FIELD-space dots in the top + bottom grandstand bands). */
  function buildStands(): void {
    const W = FIELD.width;
    const H = FIELD.height;
    const hash = (n: number): number => {
      const s = Math.sin(n * 45.3 + 9.1) * 7219.13;
      return s - Math.floor(s);
    };
    const dots: typeof standDots = [];
    let i = 1;
    for (const band of [{ y0: 5, y1: STAND_DEPTH }, { y0: H - STAND_DEPTH, y1: H - 5 }]) {
      for (let y = band.y0; y < band.y1; y += 8.5) {
        for (let x = 9; x < W - 9; x += 12) {
          dots.push({
            x: x + (hash(i) - 0.5) * 6,
            y: y + (hash(i + 1) - 0.5) * 4.5,
            r: 1.9 + hash(i + 2) * 0.9,
            color: SHIRTS[Math.floor(hash(i + 3) * SHIRTS.length)] ?? '#cccccc',
            phase: hash(i + 4) * TWO_PI,
          });
          i += 1;
        }
      }
    }
    standDots = dots;
  }

  /** Grandstands at both ends (point-symmetric) full of crowd; they LIGHT UP + BOB on a
   *  cheer (`cheer` 0..1 scales the reaction — birdie < eagle < ace). */
  function drawStands(cheer: number, now: number): void {
    if (standDots.length === 0) buildStands();
    const x = offsetX;
    const w = sl(FIELD.width);
    const top0 = sy(0);
    const topEdge = sy(STAND_DEPTH);
    const botEdge = sy(FIELD.height - STAND_DEPTH);
    const bot1 = sy(FIELD.height);
    // bleacher structure (dark) behind the crowd
    c.fillStyle = '#1d272e';
    c.fillRect(x, top0, w, topEdge - top0);
    c.fillRect(x, botEdge, w, bot1 - botEdge);
    // front rail
    c.strokeStyle = 'rgba(255,255,255,0.12)';
    c.lineWidth = sl(1.5);
    c.beginPath();
    c.moveTo(x, topEdge);
    c.lineTo(x + w, topEdge);
    c.moveTo(x, botEdge);
    c.lineTo(x + w, botEdge);
    c.stroke();
    // crowd — pixel-art spectators (cycled across the 8 variants), or dots as a fallback
    const bobAmp = cheer * sl(2.4);
    if (sprites !== null && sprites.crowd.length > 0) {
      const variants = sprites.crowd;
      const hh = sl(SPRITE.crowdH);
      standDots.forEach((d, i) => {
        const img = variants[i % variants.length];
        if (!img) return;
        const ww = hh * (img.width / Math.max(1, img.height));
        const bob = cheer > 0 ? Math.sin(now * 11 + d.phase) * bobAmp : 0;
        c.drawImage(img, sx(d.x) - ww / 2, sy(d.y) + bob - hh * 0.6, ww, hh);
      });
    } else {
      for (const d of standDots) {
        const bob = cheer > 0 ? Math.sin(now * 11 + d.phase) * bobAmp : 0;
        c.fillStyle = d.color;
        c.beginPath();
        c.arc(sx(d.x), sy(d.y) + bob, sl(d.r * 2), 0, TWO_PI);
        c.fill();
      }
    }
    // a warm glow when the crowd is cheering
    if (cheer > 0.02) {
      c.fillStyle = `rgba(255,244,196,${0.2 * cheer})`;
      c.fillRect(x, top0, w, topEdge - top0);
      c.fillRect(x, botEdge, w, bot1 - botEdge);
    }
  }

  /** How loud the crowd is right now (0..1): the biggest lingering birdie+/eagle/ace,
   *  on a quick-attack, ~1.7s-decay envelope. Birdie < eagle < ace. */
  function cheerLevel(state: GameState): number {
    let lvl = 0;
    for (const b of state.balls) {
      const base = b.scoreLabel === 'ace' ? 1 : b.scoreLabel === 'eagle' ? 0.75 : b.scoreLabel === 'birdie' ? 0.45 : 0;
      if (base === 0) continue;
      const el = BALL_REST_SECONDS - b.restRemaining;
      const env = Math.min(1, el / 0.25) * Math.max(0, Math.min(1, (1.7 - el) / 0.6));
      lvl = Math.max(lvl, base * env);
    }
    return lvl;
  }

  /** Birdie: a few little birds flutter up + away from the green, then fade (~2s). */
  function drawBirds(pin: Vec2, elapsed: number): void {
    const DUR = 2;
    if (elapsed > DUR) return;
    const cx = sx(pin.x);
    const cy = sy(pin.y);
    const hash = (n: number): number => {
      const s = Math.sin(n * 33.7 + 1.3) * 4711.1;
      return s - Math.floor(s);
    };
    for (let i = 0; i < 5; i++) {
      const ang = -Math.PI / 2 + (hash(i) - 0.5) * 1.7;
      const sp = sl(55 + hash(i + 5) * 55);
      const bx = cx + Math.cos(ang) * sp * elapsed + (hash(i + 9) - 0.5) * sl(34);
      const by = cy + Math.sin(ang) * sp * elapsed - sl(8);
      const alpha = Math.min(1, elapsed / 0.2) * Math.max(0, Math.min(1, (DUR - elapsed) / 0.5));
      if (alpha <= 0) continue;
      const flap = Math.sin(elapsed * 16 + i * 1.7) * 0.5 + 0.5;
      const s = sl(4.5);
      const dip = s * (0.2 + flap * 0.5);
      c.globalAlpha = alpha;
      c.strokeStyle = '#34424e';
      c.lineWidth = Math.max(1, sl(1.4));
      c.lineCap = 'round';
      c.beginPath();
      c.moveTo(bx - s, by - dip);
      c.quadraticCurveTo(bx - s * 0.3, by - s * 0.7, bx, by);
      c.quadraticCurveTo(bx + s * 0.3, by - s * 0.7, bx + s, by - dip);
      c.stroke();
    }
    c.globalAlpha = 1;
    c.lineCap = 'butt';
  }

  /** Eagle: a lone eagle soars across over the green, then fades (~2.3s). */
  function drawEagle(pin: Vec2, elapsed: number): void {
    const DUR = 2.3;
    if (elapsed > DUR) return;
    const x0 = offsetX - sl(30);
    const x1 = offsetX + sl(FIELD.width) + sl(30);
    const x = x0 + (x1 - x0) * (elapsed / DUR);
    const y = sy(pin.y) - sl(34) + Math.sin(elapsed * 1.8) * sl(14);
    const alpha = Math.min(1, elapsed / 0.2) * Math.max(0, Math.min(1, (DUR - elapsed) / 0.4));
    if (alpha <= 0) return;
    const s = sl(17);
    const up = s * (0.42 + Math.sin(elapsed * 3.5) * 0.18); // wing dihedral / soar
    c.globalAlpha = alpha;
    c.fillStyle = '#2a333b';
    c.beginPath();
    c.moveTo(x - s, y - up * 0.4);
    c.quadraticCurveTo(x - s * 0.45, y - up, x, y - s * 0.06);
    c.quadraticCurveTo(x + s * 0.45, y - up, x + s, y - up * 0.4);
    c.quadraticCurveTo(x + s * 0.4, y + s * 0.12, x, y + s * 0.16);
    c.quadraticCurveTo(x - s * 0.4, y + s * 0.12, x - s, y - up * 0.4);
    c.closePath();
    c.fill();
    c.beginPath();
    c.arc(x, y - s * 0.12, s * 0.13, 0, TWO_PI);
    c.fill();
    c.globalAlpha = 1;
  }

  /** A short floating result ("Birdie!", "Double Bogey…") above a ball that just came
   *  to rest: a quick scale-in, a gentle float up, then a fade — for SCORE_POPUP_DURATION. */
  function drawScorePopup(ball: Ball): void {
    if (ball.scoreLabel == null) return; // null/undefined ⇒ no score yet (in flight)
    const info = SCORE_POPUP[ball.scoreLabel];
    const total = ball.phase === 'sinking' ? BALL_SINK_SECONDS : BALL_REST_SECONDS;
    const elapsed = total - ball.restRemaining;
    if (elapsed > SCORE_POPUP_DURATION) return;
    const fadeIn = Math.min(1, elapsed / 0.12);
    const fadeOut = Math.min(1, (SCORE_POPUP_DURATION - elapsed) / 0.4);
    const alpha = Math.max(0, Math.min(fadeIn, fadeOut));
    if (alpha <= 0) return;

    const rise = sl(8) + sl(16) * smoothstep(Math.min(1, elapsed / 0.6)); // floats up, eases out
    const pop = 0.72 + 0.28 * Math.min(1, elapsed / 0.14); // quick scale-in
    const x = sx(ball.pos.x);
    const y = sy(ball.pos.y) - sl(14) - rise;

    c.save();
    c.globalAlpha = alpha;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.font = `800 ${sl(info.size * pop)}px Inter, system-ui, sans-serif`;
    c.lineJoin = 'round';
    c.lineWidth = sl(3.5);
    c.strokeStyle = 'rgba(8,20,14,0.7)'; // dark outline for legibility on any turf
    c.strokeText(info.text, x, y);
    c.fillStyle = info.color;
    c.fillText(info.text, x, y);
    c.restore();
  }

  function drawBall(ball: Ball, alpha: number): void {
    // Water: a quick splash, then the ball shrinks + fades as it sinks under.
    if (ball.phase === 'sinking') {
      drawSink(ball);
      return;
    }
    // Interpolate the GROUND position forward by `alpha` of one step along the
    // heading so motion looks smooth between fixed sim steps (read-only viz).
    let fx = ball.pos.x;
    let fy = ball.pos.y;
    let lift = 0; // 0 on the ground; arcs to 1 at mid-flight
    let stepLen = 0; // interpolated flight advance this frame (field px)
    // The ball only flies the CARRY portion of the shot; the arc completes there.
    const carryDist = ball.target * FLIGHT.carryFraction;
    if (ball.phase === 'flight') {
      const remain = Math.max(0, ball.target - ball.travelled);
      const spd = Math.max(1.4, remain * 0.06); // mirrors FLIGHT step
      const carryRemain = Math.max(0, carryDist - ball.travelled); // don't overshoot the carry
      stepLen = Math.min(spd * alpha, carryRemain);
      fx += Math.cos(ball.angle) * stepLen;
      fy += Math.sin(ball.angle) * stepLen;
      // Parabolic arc over the CARRY (lands at the carry point, then it rolls out).
      const t = carryDist > 0 ? Math.min(1, (ball.travelled + stepLen) / carryDist) : 1;
      lift = 4 * t * (1 - t);
    }
    const bx = sx(fx); // ground x
    const by = sy(fy); // ground y — where the shadow sits
    const rival = ball.side === 'rival';

    // Flight height in screen px: a distance-scaled arc (longer shots fly higher), capped.
    const peakField = Math.min(carryDist * 0.22, FIELD.height * 0.09);
    const heightPx = sl(peakField) * lift;
    const ballY = by - heightPx; // the ball lifts above its shadow

    // --- ground shadow: directly under the ball, growing + fading as it rises ---
    c.fillStyle = `rgba(0,0,0,${0.34 - 0.18 * lift})`;
    c.beginPath();
    c.arc(bx, by, sl(4) * (1 + 0.9 * lift), 0, TWO_PI);
    c.fill();

    // --- motion trail behind the (raised) ball ---
    c.strokeStyle = rival ? 'rgba(255,180,173,0.4)' : 'rgba(255,255,255,0.4)';
    c.lineWidth = sl(2);
    c.beginPath();
    const trailLen = sl(ball.phase === 'flight' ? 22 : 8);
    c.moveTo(bx - Math.cos(ball.angle) * trailLen, ballY - Math.sin(ball.angle) * trailLen);
    c.lineTo(bx, ballY);
    c.stroke();

    // --- ball: dimpled + ROLLING. The dimple texture scrolls along the heading by
    // the ground distance covered (rolling without slipping), so it visibly rolls in
    // flight and keeps rolling — decelerating — through the green's slope roll. ---
    const r = sl(6) * (1 + 0.25 * lift);
    // Ground distance rolled so far (screen px). Flight: travelled + interp (air
    // spin). Roll-out: the actual remaining distance, eased in by the roll frames
    // (matches the physics ease 1 − k(k+1)/(N(N+1))). Settled: the full roll-out.
    const rollOut = ball.target - ball.travelled; // ground roll-out distance (field px)
    let roll = sl(ball.travelled + stepLen);
    if (ball.phase === 'rolling') {
      const k = ball.rollFramesLeft;
      const n = ROLL.frames;
      const applied = 1 - (k * (k + 1)) / (n * (n + 1));
      roll = sl(ball.travelled) + sl(rollOut) * applied;
    } else if (ball.phase === 'settled') {
      roll = sl(ball.travelled) + sl(rollOut);
    }

    if (sprites !== null) {
      // pixel-art ball sprite (a touch larger than the vector radius so the outline reads)
      const d = r * 2.4;
      c.drawImage(sprites.ball, bx - d / 2, ballY - d / 2, d, d);
      return;
    }

    // base
    c.fillStyle = rival ? '#ffd2cd' : '#ffffff';
    c.beginPath();
    c.arc(bx, ballY, r, 0, TWO_PI);
    c.fill();

    // dimples — a hex grid scrolling in the heading direction (clipped to the ball)
    c.save();
    c.beginPath();
    c.arc(bx, ballY, r, 0, TWO_PI);
    c.clip();
    c.translate(bx, ballY);
    c.rotate(ball.angle); // local +x = direction of travel
    const spacing = r * 0.58;
    const dimR = r * 0.17;
    const off = ((roll % spacing) + spacing) % spacing; // forward scroll, wrapped
    c.fillStyle = rival ? 'rgba(120,42,38,0.32)' : 'rgba(22,42,74,0.22)';
    for (let gx = -3; gx <= 3; gx++) {
      for (let gy = -3; gy <= 3; gy++) {
        const px = gx * spacing + off + (gy & 1 ? spacing / 2 : 0); // staggered rows
        c.beginPath();
        c.arc(px, gy * spacing, dimR, 0, TWO_PI);
        c.fill();
      }
    }
    // soft spherical shading (top-left light → bottom-right dark) for volume
    const shade = c.createRadialGradient(-r * 0.35, -r * 0.35, r * 0.15, 0, 0, r * 1.05);
    shade.addColorStop(0, 'rgba(255,255,255,0.30)');
    shade.addColorStop(0.55, 'rgba(255,255,255,0)');
    shade.addColorStop(1, 'rgba(0,0,0,0.26)');
    c.fillStyle = shade;
    c.fillRect(-r, -r, r * 2, r * 2);
    c.restore();

    // outline
    c.strokeStyle = 'rgba(0,0,0,0.3)';
    c.lineWidth = sl(1);
    c.beginPath();
    c.arc(bx, ballY, r, 0, TWO_PI);
    c.stroke();
  }

  /** A cute top-of-the-grass GOPHER billboard at feet baseline (cx, cy), facing ±1, optionally
   *  holding the stolen ball; `scale` shrinks it as it dives back into its hole. */
  function drawGopherSprite(cx: number, cy: number, facing: number, holding: boolean, scale: number): void {
    if (scale <= 0.06) return;
    if (sprites !== null) {
      const img = sprites.gopher;
      const hh = sl(SPRITE.gopherH) * scale; // height
      const ww = hh * (img.width / Math.max(1, img.height));
      // soft contact shadow
      c.fillStyle = 'rgba(0,0,0,0.2)';
      c.beginPath();
      c.ellipse(cx, cy + sl(1.5) * scale, ww * 0.42, hh * 0.12, 0, 0, TWO_PI);
      c.fill();
      c.save();
      c.translate(cx, cy);
      c.scale(facing < 0 ? -1 : 1, 1); // face the way it ran in
      c.drawImage(img, -ww / 2, -hh, ww, hh); // feet/paws at the baseline (cy)
      c.restore();
      if (holding) c.drawImage(sprites.ball, cx - sl(4.5) * scale, cy - sl(8) * scale, sl(9) * scale, sl(9) * scale);
      return;
    }
    const u = sl(4) * scale; // doubled size
    const brown = '#9c6b3f';
    const belly = '#d8b487';
    // shadow
    c.fillStyle = 'rgba(0,0,0,0.2)';
    c.beginPath();
    c.ellipse(cx, cy + u * 0.5, u * 3.4, u * 1.4, 0, 0, TWO_PI);
    c.fill();
    // torso + belly
    c.fillStyle = brown;
    c.beginPath();
    c.ellipse(cx, cy - u * 3.2, u * 2.6, u * 3.4, 0, 0, TWO_PI);
    c.fill();
    c.fillStyle = belly;
    c.beginPath();
    c.ellipse(cx, cy - u * 2.6, u * 1.5, u * 2.2, 0, 0, TWO_PI);
    c.fill();
    // arms (forward + holding a ball, or a little running paw)
    c.fillStyle = brown;
    if (holding) {
      c.beginPath();
      c.arc(cx + facing * u * 2.1, cy - u * 3, u * 0.8, 0, TWO_PI);
      c.fill();
      c.fillStyle = '#ffffff';
      c.beginPath();
      c.arc(cx + facing * u * 3, cy - u * 3.1, u * 1.1, 0, TWO_PI);
      c.fill();
      c.strokeStyle = 'rgba(0,0,0,0.28)';
      c.lineWidth = Math.max(1, u * 0.3);
      c.stroke();
    } else {
      c.beginPath();
      c.arc(cx + facing * u * 1.9, cy - u * 3.4, u * 0.7, 0, TWO_PI);
      c.fill();
    }
    // head
    c.fillStyle = brown;
    c.beginPath();
    c.arc(cx, cy - u * 6.4, u * 2.4, 0, TWO_PI);
    c.fill();
    c.beginPath(); // ears
    c.arc(cx - u * 1.7, cy - u * 8, u * 0.9, 0, TWO_PI);
    c.arc(cx + u * 1.7, cy - u * 8, u * 0.9, 0, TWO_PI);
    c.fill();
    // muzzle
    c.fillStyle = belly;
    c.beginPath();
    c.ellipse(cx + facing * u * 0.2, cy - u * 5.6, u * 1.3, u, 0, 0, TWO_PI);
    c.fill();
    // eyes
    c.fillStyle = '#21160e';
    c.beginPath();
    c.arc(cx - u * 0.8 + facing * u * 0.3, cy - u * 6.9, u * 0.36, 0, TWO_PI);
    c.arc(cx + u * 0.9 + facing * u * 0.3, cy - u * 6.9, u * 0.36, 0, TWO_PI);
    c.fill();
    // nose + buck teeth
    c.fillStyle = '#3a241a';
    c.beginPath();
    c.arc(cx + facing * u * 0.2, cy - u * 5.9, u * 0.45, 0, TWO_PI);
    c.fill();
    c.fillStyle = '#ffffff';
    c.fillRect(cx - u * 0.45 + facing * u * 0.2, cy - u * 5.4, u * 0.4, u * 0.85);
    c.fillRect(cx + u * 0.08 + facing * u * 0.2, cy - u * 5.4, u * 0.4, u * 0.85);
  }

  /** The GOPHER scampering IN while a gopher'd ball is still in the air / rolling — it runs from
   *  the nearer screen edge to the ball, parameterised by flight progress so it ARRIVES as the
   *  ball lands (≈2s sooner than waiting for the ball to settle first). */
  function drawGopherIncoming(ball: Ball): void {
    const carry = ball.target * FLIGHT.carryFraction || 1;
    const p = Math.max(0, Math.min(1, ball.travelled / carry)); // 0 at launch → 1 at touchdown
    const ease = smoothstep(p);
    const bx = sx(ball.pos.x);
    const by = sy(ball.pos.y);
    const fromLeft = ball.pos.x < FIELD.width / 2;
    const edgeX = fromLeft ? offsetX - sl(28) : offsetX + sl(FIELD.width) + sl(28);
    const gx = edgeX + (bx - edgeX) * ease;
    const bob = Math.abs(Math.sin(p * Math.PI * 9)) * sl(3) * (1 - p); // a running bounce, settling
    drawGopherSprite(gx, by - bob, fromLeft ? 1 : -1, false, 1);
  }

  /** The GOPHER steal once the ball has settled: it's already arrived (it ran in during the
   *  flight), so it SNATCHES the ball, laughs ("Hee hee!") with the "Double Par!" verdict, then
   *  dives back into its hole with the ball. Driven by the ball's linger timer. */
  function drawGopherSteal(ball: Ball): void {
    const total = GOPHER.lingerSeconds;
    const elapsed = total - ball.restRemaining; // 0 → total
    const bx = sx(ball.pos.x);
    const by = sy(ball.pos.y);
    const facing = ball.pos.x < FIELD.width / 2 ? 1 : -1;
    const snatch = 0.18; // the ball is visibly grabbed over this beat, then it's gone

    // The ball is still there for the snatch beat (being grabbed), then the gopher has it.
    if (elapsed < snatch) {
      c.fillStyle = ball.side === 'rival' ? '#ffd2cd' : '#ffffff';
      c.beginPath();
      c.arc(bx, by, sl(6), 0, TWO_PI);
      c.fill();
      c.strokeStyle = 'rgba(0,0,0,0.3)';
      c.lineWidth = sl(1);
      c.stroke();
    }

    // After the snatch: a laughing wobble, then a dive into the hole (shrink + sink) at the end.
    let dx = 0;
    let sink = 0;
    let scale = 1;
    const stolen = elapsed >= snatch;
    if (stolen) {
      const lt = (elapsed - snatch) / (total - snatch); // 0 → 1 over the laugh+dive
      if (lt < 0.65) dx = Math.sin(elapsed * 32) * sl(1.8); // shaking with laughter
      else {
        const d = (lt - 0.65) / 0.35;
        sink = sl(14) * d;
        scale = Math.max(0, 1 - 0.95 * d);
      }
    }
    drawGopherSprite(bx + dx, by + sink, facing, true, scale); // holding the ball

    // "Hee hee!" + the verdict appear ONLY after the snatch (once he's actually stolen it).
    if (stolen && scale > 0.2) {
      const a = Math.min(1, (elapsed - snatch) / 0.15);
      c.globalAlpha = a;
      c.textAlign = 'center';
      c.lineJoin = 'round';
      c.strokeStyle = 'rgba(8,20,14,0.7)';
      // gopher's cackle
      c.font = `800 ${sl(11)}px Inter, system-ui, sans-serif`;
      c.lineWidth = sl(3);
      const ly = by - sl(30) + sink;
      c.strokeText('Hee hee!', bx + dx, ly);
      c.fillStyle = '#ffe14d';
      c.fillText('Hee hee!', bx + dx, ly);
      // verdict
      c.font = `800 ${sl(14)}px Inter, system-ui, sans-serif`;
      c.lineWidth = sl(3.5);
      const vy = by - sl(46);
      c.strokeText('Double Par!', bx, vy);
      c.fillStyle = '#ff7a6b';
      c.fillText('Double Par!', bx, vy);
      c.globalAlpha = 1;
    }
  }

  function drawWind(state: GameState): void {
    const w = state.wind;
    if (w.remaining <= 0) return;
    const cx = sx(FIELD.width * 0.5);
    const cy = sy(FIELD.height * 0.5);
    const ang = Math.atan2(w.accel.y, w.accel.x);

    c.save();
    c.globalAlpha = 0.85;
    c.translate(cx, cy);
    c.rotate(ang);
    c.strokeStyle = 'rgba(188,214,255,0.85)';
    c.lineWidth = sl(4);
    c.lineCap = 'round';
    const tail = sl(26);
    const tip = sl(20);
    const barb = sl(8);
    c.beginPath();
    c.moveTo(-tail, 0);
    c.lineTo(tip, 0);
    c.moveTo(tip, 0);
    c.lineTo(tip - sl(10), -barb);
    c.moveTo(tip, 0);
    c.lineTo(tip - sl(10), barb);
    c.stroke();
    c.restore();
    c.globalAlpha = 1;

    c.fillStyle = '#bcd6ff';
    c.font = `bold ${sl(11)}px 'JetBrains Mono', ui-monospace, monospace`;
    c.textAlign = 'center';
    c.fillText(`WIND ${Math.ceil(w.remaining)}s`, cx, cy - sl(18));
  }

  /**
   * Aim line + the two-stage swing meter drawn ALONG the aim line (§3.4). Only the
   * human (`you`) side shows UI; the AI fires directly. The aim line draws during
   * aim/backswing/impact; the meter (POWER/IMPACT zones + sliding marker) is overlaid
   * on the line during backswing/impact so the player's eye stays on the shot.
   */
  function drawSwing(state: GameState): void {
    const swing = state.swing;
    if (swing.side !== 'you') return;
    if (swing.phase !== 'aim' && swing.phase !== 'backswing' && swing.phase !== 'impact') return;
    if (swing.clubIdx === null) return;

    const club = state.you.offenseHand[swing.clubIdx];
    if (club === undefined) return;
    const launch = state.you.tees[boxForPower(club.power)].pos; // POWER picks the launch tee
    const tx = sx(launch.x);
    const ty = sy(launch.y);

    // --- aim line: the tee's SET max range in the aim direction (Golf-Clash dial-it-in).
    // The line no longer snaps to the hole; the hole's DISTANCE is marked at its fraction h
    // along the line, and the player times the power to it. ---
    const aimAngle = swing.aimAngle;
    let ex = tx;
    let ey = ty;
    let holeFrac = 1;
    if (aimAngle !== null) {
      const pin = state.rival.green.pin;
      const reach = SHOT_RANGE[boxForPower(club.power)]; // the tee's SET max range (field px)
      const pinDist = Math.hypot(launch.x - pin.x, launch.y - pin.y);
      holeFrac = Math.min(1, reach > 0 ? pinDist / reach : 1);
      // SHORTENED aim/meter bar: drawn to a fraction of the SET range so it stops well
      // short of the hole. The power band still sits at the hole's fraction h ALONG this
      // bar, so you dial the power to it — the line never reaches the hole.
      const lineLen = pinDist * SWING_LINE_FRAC;
      ex = sx(launch.x + Math.cos(aimAngle) * lineLen);
      ey = sy(launch.y + Math.sin(aimAngle) * lineLen);
      c.strokeStyle = swing.phase === 'aim' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.4)';
      c.lineWidth = sl(2);
      c.setLineDash([sl(5), sl(6)]);
      c.beginPath();
      c.moveTo(tx, ty);
      c.lineTo(ex, ey);
      c.stroke();
      c.setLineDash([]);
    }
    // (No tee marker dot — the golfer standing on the tee is the launch-spot marker now.)

    if (swing.phase === 'aim' || aimAngle === null) return; // meter only once swinging

    drawLineMeter(swing, club, holeFrac, tx, ty, ex, ey);
  }

  /**
   * The two-stage meter drawn ALONG the aim line (§3.4). The meter value 0..1 maps
   * to a point between the tee (0) and the target ring (1); the wide green POWER
   * zone and tight gold IMPACT zone are bands on the line, and the marker slides
   * along it toward the target.
   */
  function drawLineMeter(swing: SwingState, club: Club, holeFrac: number, tx: number, ty: number, ex: number, ey: number): void {
    // POWER (distance) target sits at the HOLE's fraction along the line — you time the power
    // to the hole. Per-card: the POWER band widens with CONTROL (forgiving distance timing);
    // the IMPACT (direction) band widens with ACCURACY (a wider clean zone = easier to strike
    // straight). Tapping inside a band is a clean strike — perfect timing aces.
    const powerHalf = powerBandHalf(club.power, club.control);
    const impactHalf = SWING.impBand * CARD_MAP.impactBand(club.accuracy);
    const powerCenter = holeFrac;

    const lerpX = (v: number): number => tx + (ex - tx) * v;
    const lerpY = (v: number): number => ty + (ey - ty) * v;
    const dx = ex - tx;
    const dy = ey - ty;
    const len = Math.hypot(dx, dy) || 1;
    const perpX = -dy / len; // unit perpendicular (screen space)
    const perpY = dx / len;

    // A sweet-spot band: an edge→core→edge gradient so the hue shifts and brightens
    // toward the PERFECT centre, guiding the eye to the exact lock point.
    const band = (center: number, half: number, edge: string, core: string, width: number): void => {
      const v0 = center - half;
      const v1 = center + half;
      const g = c.createLinearGradient(lerpX(v0), lerpY(v0), lerpX(v1), lerpY(v1));
      g.addColorStop(0, edge);
      g.addColorStop(0.5, core);
      g.addColorStop(1, edge);
      c.strokeStyle = g;
      c.lineWidth = width;
      c.lineCap = 'round';
      c.beginPath();
      c.moveTo(lerpX(v0), lerpY(v0));
      c.lineTo(lerpX(v1), lerpY(v1));
      c.stroke();
    };
    // POWER zone (at the hole) in BLUE; IMPACT zone (tight) in gold — per box.
    band(powerCenter, powerHalf, METER.powerEdge, METER.powerCore, sl(13));
    band(SWING.impSweet, impactHalf, METER.impactEdge, METER.impactCore, sl(15));
    c.lineCap = 'butt';

    // zone labels, offset to the side of the line, tinted to match each zone
    c.font = `${sl(7)}px Inter, system-ui, sans-serif`;
    c.textAlign = 'center';
    c.fillStyle = METER.powerMark;
    c.fillText('POWER', lerpX(powerCenter) + perpX * sl(13), lerpY(powerCenter) + perpY * sl(13));
    c.fillStyle = METER.impactMark;
    c.fillText('IMPACT', lerpX(SWING.impSweet) + perpX * sl(13), lerpY(SWING.impSweet) + perpY * sl(13));

    // locked backswing tick (where power was set), shown during impact
    if (swing.phase === 'impact' && swing.backswingMeter !== null) {
      const bx = lerpX(swing.backswingMeter);
      const by = lerpY(swing.backswingMeter);
      c.strokeStyle = METER.powerMark;
      c.lineWidth = sl(3);
      c.beginPath();
      c.moveTo(bx + perpX * sl(9), by + perpY * sl(9));
      c.lineTo(bx - perpX * sl(9), by - perpY * sl(9));
      c.stroke();
    }

    // sliding marker (green when inside the active zone, white otherwise)
    const mx = lerpX(swing.meter);
    const my = lerpY(swing.meter);
    const inBack =
      swing.phase === 'backswing' && Math.abs(swing.meter - powerCenter) <= powerHalf;
    const inImp = swing.phase === 'impact' && Math.abs(swing.meter - SWING.impSweet) <= impactHalf;
    // Marker glows in the ACTIVE sweet-spot's colour (blue for power, gold for impact).
    const markColor = inBack ? METER.powerMark : inImp ? METER.impactMark : '#ffffff';
    c.strokeStyle = markColor;
    c.lineWidth = sl(4);
    c.beginPath();
    c.moveTo(mx + perpX * sl(10), my + perpY * sl(10));
    c.lineTo(mx - perpX * sl(10), my - perpY * sl(10));
    c.stroke();
    c.fillStyle = markColor;
    c.beginPath();
    c.arc(mx, my, sl(5), 0, TWO_PI);
    c.fill();
    c.strokeStyle = 'rgba(0,0,0,0.55)';
    c.lineWidth = sl(1.5);
    c.stroke();

    // prompt near the tee end of the line
    c.fillStyle = '#ffffff';
    c.font = `bold ${sl(10)}px 'JetBrains Mono', ui-monospace, monospace`;
    c.textAlign = 'center';
    c.fillText(swing.phase === 'backswing' ? 'TAP: power' : 'TAP: impact', tx + perpX * sl(20), ty + perpY * sl(20));
  }

  // -------------------------------------------------------------------------
  // render() — the hot path
  // -------------------------------------------------------------------------

  /**
   * Blit the finished low-res pixel buffer onto the canvas with smoothing OFF — the nearest-
   * neighbor upscale is what turns the scene into crisp chunky 8-bit pixels. No-op when we drew
   * straight to the screen (the headless render harness has no offscreen buffer).
   */
  function present(): void {
    if (pixelBuffer === null || bufferCtx === null) return;
    screen.setTransform(1, 0, 0, 1, 0, 0);
    screen.imageSmoothingEnabled = false;
    screen.clearRect(0, 0, canvas.width, canvas.height);
    screen.drawImage(pixelBuffer, 0, 0, bufW, bufH, 0, 0, canvas.width, canvas.height);
  }

  function render(state: GameState, alpha: number): void {
    // Draw the scene into the active context (the pixel buffer, or the screen when headless). The
    // buffer renders in CSS px at 1/PIXEL resolution (sceneScale); present() upscales it after.
    c.setTransform(sceneScale, 0, 0, sceneScale, 0, 0);
    c.clearRect(0, 0, view.width, view.height);
    c.imageSmoothingEnabled = false; // crisp nearest-neighbor scaling for the pixel-art sprites

    drawFrame();
    drawTurf();

    // Field contents are clipped to the letterbox rectangle.
    c.save();
    c.beginPath();
    c.rect(offsetX, offsetY, sl(FIELD.width), sl(FIELD.height));
    c.clip();

    // Grandstands frame both ends (point-symmetric); the crowd reacts to the play.
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
    drawStands(cheerLevel(state), now);

    // Hazards sit in the rough (under every manicured surface), then fairways.
    drawHazards(state);
    drawFairway(state.you.fairway, true);
    drawFairway(state.rival.fairway, false);

    drawGreenLabels(state);

    drawGreen(state.rival.green, false);
    drawGreen(state.you.green, true);

    // Decorative trees frame the rough (over the turf, under the in-air balls).
    drawTrees(state);

    // Tee-off spots: a pad + little golfer at every tee (long/mid/short, both sides); a roughed
    // tee shows a rough tuft instead of the clean pad.
    drawTeeSpots(state);

    // A holed ace DROPS into the cup; a gopher'd ball gets snatched; everything else draws normally.
    for (const ball of state.balls) {
      if (ball.phase === 'settled' && ball.scoreLabel === 'ace') {
        const pin = (ball.side === 'you' ? state.rival : state.you).green.pin;
        drawAceDrop(pin, BALL_REST_SECONDS - ball.restRemaining);
      } else if (ball.phase === 'settled' && ball.stolen) {
        drawGopherSteal(ball);
      } else {
        drawBall(ball, alpha);
      }
    }
    // A gopher'd ball gets a gopher scampering IN over the ball's flight (it arrives as the ball
    // lands), drawn on top of the flying ball.
    for (const ball of state.balls) {
      if (ball.stolen && (ball.phase === 'flight' || ball.phase === 'rolling')) drawGopherIncoming(ball);
    }
    // Floating score popups sit above every ball.
    for (const ball of state.balls) drawScorePopup(ball);
    // Celebrations above everything: birds (birdie), a soaring eagle (eagle), confetti (ace).
    for (const ball of state.balls) {
      if (ball.phase !== 'settled') continue;
      const el = BALL_REST_SECONDS - ball.restRemaining;
      const pin = (ball.side === 'you' ? state.rival : state.you).green.pin;
      if (ball.scoreLabel === 'birdie') drawBirds(pin, el);
      else if (ball.scoreLabel === 'eagle') drawEagle(pin, el);
      else if (ball.scoreLabel === 'ace') drawConfetti(pin, el);
    }

    drawWind(state);
    drawSwing(state);

    c.restore();

    // Upscale the low-res buffer onto the canvas (nearest-neighbor) → the crisp 8-bit grid.
    present();
  }

  // First layout pass so the renderer is usable immediately.
  resize();

  return {
    render,
    resize,
    screenToField,
    fieldToScreen,
    setSprites(s): void {
      sprites = s;
    },
    setLeftHanded(left): void {
      leftHanded = left;
    },
  };
}

// ---------------------------------------------------------------------------
// Backward-compatible alias for the Phase 0 stub name (overseer may still import
// either). The canonical seam is `createRenderer` / `Renderer` above.
// ---------------------------------------------------------------------------

/** @deprecated Use {@link Renderer}. Kept so the Phase 0 stub import keeps working. */
export type CanvasRenderer = Renderer;
/** @deprecated Use {@link createRenderer}. */
export const createCanvasRenderer = createRenderer;
